import { useEffect, useRef, useState } from 'react';
import { initTabPresence } from '../../utils/authGuard';

type PresenceState = 'active' | 'blocked';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.72)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100000,
  padding: '1rem',
};

const cardStyle: React.CSSProperties = {
  maxWidth: '440px',
  width: '100%',
  background: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  padding: '2rem',
  textAlign: 'center',
  fontFamily: 'inherit',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#0f172a',
  marginBottom: '0.5rem',
};

const bodyStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: '#475569',
  lineHeight: 1.5,
  marginBottom: '1.5rem',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  justifyContent: 'center',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  background: '#1e40af',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: '#94a3b8',
  marginTop: '1rem',
};

/**
 * Enforces a SINGLE ACTIVE TAB of the app per browser profile.
 *
 * LMMS runs its own internal (MDI-style) tab system inside one browser tab, so
 * opening the app in multiple browser tabs is intentionally disallowed —
 * multiple browser tabs would mean duplicate sessions, duplicate API calls,
 * and inconsistent client state.
 *
 * Model (the WhatsApp-Web / "use here" pattern):
 *   - The first tab is active. Opening a second tab shows a passive, blocking
 *     screen: "LMMS is open in another tab" with a single "Use here" action.
 *   - "Use here" takes over IN PLACE (no reload): it broadcasts a takeover, so
 *     the other tab flips to the same blocked screen, and this tab activates.
 *   - There is deliberately NO "close this tab" button — browsers cannot close
 *     a user-opened tab from script (the old attempt blanked the page to
 *     about:blank and created a Close/Reactivate loop). The user closes the
 *     extra tab themselves (Ctrl+W / ⌘W), or clicks "Use here".
 *   - If the active tab is actually closed, the blocked tab auto-promotes
 *     (via the presence "leaving" broadcast) so it is never stranded.
 *
 * Mounted once, only inside the authenticated shell (see TabPresenceGate).
 */
export function TabTakeoverGuard() {
  const [state, setState] = useState<PresenceState>('active');
  const takeOverRef = useRef<(() => void) | null>(null);
  const stateRef = useRef<PresenceState>('active');
  stateRef.current = state;

  useEffect(() => {
    const presence = initTabPresence({
      onExistingTabDetected: () => {
        // Another tab is already active — this (newer) tab yields.
        setState('blocked');
      },
      onTakeoverReceived: () => {
        // Another tab took over — this tab yields.
        setState('blocked');
      },
      onOtherTabLeft: () => {
        // The other tab closed. If we were blocked, promote ourselves so the
        // sole remaining tab is usable without any manual step.
        if (stateRef.current === 'blocked') {
          presence.takeOver();
          setState('active');
        }
      },
    });
    takeOverRef.current = presence.takeOver;
    return presence.stop;
  }, []);

  if (state === 'blocked') {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={cardStyle}>
          <div style={titleStyle}>LMMS is open in another tab</div>
          <div style={bodyStyle}>
            To keep your data consistent, LMMS runs in a single browser tab.
            This tab is currently inactive.
          </div>
          <div style={buttonRowStyle}>
            <button
              type="button"
              style={primaryBtnStyle}
              onClick={() => {
                takeOverRef.current?.();
                setState('active');
              }}
            >
              Use here
            </button>
          </div>
          <div style={hintStyle}>
            Clicking &ldquo;Use here&rdquo; will lock the other tab. Otherwise
            you can simply close this tab (Ctrl+W, or &#8984;W on Mac).
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default TabTakeoverGuard;
